import { useEffect, useState } from 'react'

import { getBillingSummary, type PackagingBudgetStatus } from '../services/api'

export interface PackagingEntitlement {
  canPackage: boolean
  reason: string | null
  /** What is left of the plan's packaging quota; null for an unbudgeted plan. */
  budget: PackagingBudgetStatus | null
  /** The plan the budget belongs to (`trial`, `free`, ...); null until the summary arrives. */
  plan: string | null
}

/**
 * Optimistic until the summary arrives: the API refuses a free tenant on its
 * own, so guessing "allowed" costs a clear 403 at submit, whereas guessing
 * "refused" would block a paying tenant on a slow request.
 */
const ALLOWED: PackagingEntitlement = { canPackage: true, reason: null, budget: null, plan: null }

const TTL_MS = 5 * 60 * 1000

let cachedAt = 0
let cached: PackagingEntitlement = ALLOWED
let inFlight: Promise<PackagingEntitlement> | null = null

/** Exported for tests, and for the moment a plan changes, which changes the budget. */
export const resetPackagingEntitlementCache = () => {
  cachedAt = 0
  cached = ALLOWED
  inFlight = null
}

const load = (): Promise<PackagingEntitlement> => {
  if (Date.now() - cachedAt < TTL_MS) return Promise.resolve(cached)
  if (inFlight) return inFlight

  inFlight = getBillingSummary()
    .then((summary) => {
      cached = {
        canPackage: summary.can_package ?? true,
        reason: summary.entitlement_reason ?? null,
        budget: summary.packaging_budget ?? null,
        plan: summary.plan ?? null,
      }
      cachedAt = Date.now()
      return cached
    })
    // A billing read that fails must not stand between a tenant and their work.
    .catch(() => ALLOWED)
    .finally(() => {
      inFlight = null
    })

  return inFlight
}

/**
 * Whether this tenant's plan includes packaging, why not when it does not, and
 * how much of a budgeted plan's quota is left.
 *
 * Shared by every modal that starts a packaging job so they all say the same
 * thing, and cached at module level so opening one does not re-ask — and so the
 * notice can be mounted anywhere without the caller providing a query client.
 */
export const usePackagingEntitlement = (): PackagingEntitlement => {
  const [entitlement, setEntitlement] = useState<PackagingEntitlement>(cached)

  useEffect(() => {
    let live = true
    load().then((value) => {
      if (live) setEntitlement(value)
    })
    return () => {
      live = false
    }
  }, [])

  return entitlement
}
